/**
 * ThemeProvider is playing a very important role in AF FED architecture.
 * All applications would get rendered inside a shadow-dom, we need to support it via 2 actions:
 * 1. Make sure JSS inject all style tags to the theme provider's root instead of the default <head/> tag.
 * 2. Make sure all modals and popover components get injected to the theme provider's root, instead of document.body
 *
 */
import React, { forwardRef, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { commonThemeSettings } from './materialTheme';
import { create } from 'jss';
import {
  StyledEngineProvider,
  createTheme,
  ThemeProvider as MuiThemeProvider
} from '@mui/material/styles';
import { jssPreset, StylesProvider } from '@mui/styles';
import createGenerateClassName from './forked/createGenerateClassName_fork';
import FontsLoader from './FontsLoader';

import injectSeedToStyles from './injectSeedToStyles';
import injectTestEnvShims from './injectTestEnvShims';
import { LicenseInfo } from '@mui/x-license-pro';

LicenseInfo.setLicenseKey(
  '5cfa78abb5b8027e74886e26b24c43fdTz03NDA0OCxFPTE3MjU1MjI0OTYwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLEtWPTI='
);

injectTestEnvShims();

const InsertionPointElement = forwardRef((props, ref) => (
  <noscript
    data-comment="insertion point for jss styles"
    {...props}
    ref={ref}
  />
));

InsertionPointElement.displayName = 'InsertionPointElement';

const applyRootContainerToThemeProps = container => ({
  MuiPopover: {
    defaultProps: {
      container
    }
  },
  MuiModal: {
    defaultProps: {
      container,
      disableEnforceFocus: true // fixes a bug see https://github.com/mui-org/material-ui/issues/24032#issuecomment-755873514
    }
  },
  MuiDrawer: {
    defaultProps: {
      container
    }
  },
  MuiTooltip: {
    defaultProps: {
      PopperProps: {
        container
      }
    }
  }
});

const ThemeProvider = ({
  children,
  shouldUseGlobalInsertionPoint = true,
  insertionPointElement,
  materialTheme = commonThemeSettings,
  createGenerateClassNameOptions = {},
  ...rest
}) => {
  const generateClassName = createGenerateClassName({
    productionPrefix: 'AF_DS',
    ...createGenerateClassNameOptions
  });

  // lazy init with the MUI default JSS preset
  const [defaultMuiJSSPreset] = useState(() => jssPreset());
  const [theme, setTheme] = useState();
  const rootRef = useRef();
  const insertionPointRef = useRef(
    insertionPointElement || defaultMuiJSSPreset.insertionPoint
  );
  const [customJSSObj, setJss] = useState();

  // create a point element only when:
  // 1. The consumer didn't pass an insertionPoint element
  // 2. The consumer didn't explicitly asked for the default insertionPoint
  const shouldRenderInsertionPoint =
    !insertionPointElement && !shouldUseGlobalInsertionPoint;

  const isReadyToRender = customJSSObj && theme;

  useLayoutEffect(() => {
    const seed = createGenerateClassNameOptions.seed;
    const overridesWithSeed = injectSeedToStyles(
      materialTheme.components,
      seed,
      { name: 'overrides (ThemeProvider)' }
    );
    const themeWithSeed = { ...materialTheme, components: overridesWithSeed };

    if (shouldUseGlobalInsertionPoint) {
      setJss(defaultMuiJSSPreset);
      setTheme(createTheme(themeWithSeed, { seed }));
    } else {
      setJss(() => {
        return {
          ...defaultMuiJSSPreset,
          // We define a custom insertion point that JSS will look for injecting the styles in the DOM.
          // At this point it should be either the passed in element OR the ref to our own created element
          insertionPoint: insertionPointRef.current
        };
      });
      // We extend (and override) commonThemeSettings, so we can set a root for all modals, tooltips etc.
      // We need this because we might get rendered inside a shadow-dom and we don't want to get attached to document.body.
      // Note: order matters, 2nd argument of createTheme overrides the first.
      setTheme(
        createTheme(themeWithSeed, {
          seed,
          components: applyRootContainerToThemeProps(rootRef.current)
        })
      );
    }
    // we don't need deps as we only want to run this effect once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div ref={rootRef} data-id="af-theme-root" {...rest}>
      {shouldRenderInsertionPoint && (
        <InsertionPointElement ref={insertionPointRef} />
      )}
      <FontsLoader />
      {isReadyToRender && (
        <StyledEngineProvider injectFirst>
          <StylesProvider
            jss={create(customJSSObj)}
            generateClassName={generateClassName}>
            <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
          </StylesProvider>
        </StyledEngineProvider>
      )}
    </div>
  );
};

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
  /** Our ThemeProvider is configured to insert the stylesheets inside the root of the render tree,
   * you can set it to use the default insertion point which is the <head/> element
   * Note that this will also make all modals, tooltips and popovers injected to document.body instead of the root.
   * */
  shouldUseGlobalInsertionPoint: PropTypes.bool,
  /** You can specify an insertion point to render the stylesheets (a string or ref to a DOM node)
   * More details in the DOCS
   * https://github.com/cssinjs/jss/blob/master/docs/setup.md#specify-the-dom-insertion-point
   */
  insertionPointElement: PropTypes.oneOfType([
    PropTypes.instanceOf(HTMLElement),
    PropTypes.string
  ]),
  materialTheme: PropTypes.object
};

export default ThemeProvider;
